import { z } from "zod";
import { Privacy } from "@/helpers/constants";
import { PrivacyAware } from "@/types";

export const KnowledgeCatalogItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  icon: z.string().optional().nullable(),
  content: z.object({
    pages: z.array(
      z.object({
        content: z.string(),
      })
    ),
  }),
  forked: z.boolean().optional(),
  // The template a forked document was created from, recorded by the
  // fork-template endpoint and untouched by renames. The backend has always
  // returned it; declaring it lets the workspace table tell which templates
  // are already in the tenant without matching on a name the user may have
  // changed.
  source: z.string().nullable().optional(),
  public_id: z.string().optional(),
  user_id: z.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  type: z.enum(["source", "template"]).default("source"),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC]).default(Privacy.PRIVATE).optional(),
});

export type KnowledgeCatalogItem = z.infer<typeof KnowledgeCatalogItemSchema> & PrivacyAware;

// Knowledge Base V2 - Document Upload & Versioning Schemas
export const KnowledgeDocumentUploadResponseSchema = z.object({
  success: z.boolean(),
  knowledge_base_id: z.number(),
  // Optional: the retry endpoint omits these (backend bug AI-9747).
  knowledge_base_public_id: z.string().optional(),
  document_id: z.number(),
  document_public_id: z.string(),
  name: z.string().optional(),
  version: z.number(),
  processing_status: z.string(),
  celery_task_id: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});

export type KnowledgeDocumentUploadResponse = z.infer<typeof KnowledgeDocumentUploadResponseSchema>;

export const KnowledgeDocumentUploadToExistingResponseSchema = z.object({
  success: z.boolean(),
  document_id: z.number(),
  document_public_id: z.string(),
  knowledge_base_id: z.number(),
  version: z.number(),
  processing_status: z.string(),
  celery_task_id: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});

export type KnowledgeDocumentUploadToExistingResponse = z.infer<
  typeof KnowledgeDocumentUploadToExistingResponseSchema
>;

export const KnowledgeBaseDetailResponseSchema = z.object({
  // Knowledge Base fields
  id: z.number(),
  public_id: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  content: z.object({
    pages: z.array(z.object({ content: z.string() })),
  }),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC]),
  user_id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  forked: z.boolean().optional().default(false),
  source: z.string().optional().nullable(),

  // Current active document fields
  current_document_id: z.number().optional().nullable(),
  current_document_public_id: z.string().optional().nullable(),
  current_version: z.number().optional().nullable(),
  processing_status: z.string().optional().nullable(),
  processing_started_at: z.string().optional().nullable(),
  processing_completed_at: z.string().optional().nullable(),
  processing_error: z.string().optional().nullable(),
  total_chunks: z.number().optional().nullable(),
  celery_task_id: z.string().optional().nullable(),
  original_filename: z.string().optional().nullable(),
  file_type: z.string().optional().nullable(),
  file_size_bytes: z.number().optional().nullable(),
  document_user_id: z.number().optional().nullable(),
  document_created_at: z.string().optional().nullable(),
  document_updated_at: z.string().optional().nullable(),

  // User-friendly status message
  status_message: z.string(),
});

export type KnowledgeBaseDetailResponse = z.infer<typeof KnowledgeBaseDetailResponseSchema>;

// Knowledge Base Document History
export const KnowledgeDocumentHistoryItemSchema = z.object({
  id: z.number(),
  public_id: z.string(),
  version: z.number(),
  is_active: z.boolean(),
  original_filename: z.string(),
  file_type: z.string(),
  file_size_bytes: z.number(),
  processing_status: z.string(),
  processing_started_at: z.string().optional().nullable(),
  processing_completed_at: z.string().optional().nullable(),
  processing_error: z.string().optional().nullable(),
  total_chunks: z.number().optional().nullable(),
  celery_task_id: z.string().optional().nullable(),
  user_id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type KnowledgeDocumentHistoryItem = z.infer<typeof KnowledgeDocumentHistoryItemSchema>;

// Knowledge Base Document Chunks
export const ChunkSchema = z.object({
  chunk_id: z.string(),
  chunk_order_index: z.number(),
  content: z.string(),
  tokens: z.number(),
  file_path: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  knowledge_type: z.string().optional().nullable(),
  code_snippets: z.array(z.any()).optional().nullable(),
  retrieval_count: z.number().optional().default(0),
});

export type Chunk = z.infer<typeof ChunkSchema>;

export const DocumentChunksResponseSchema = z.object({
  document_id: z.number(),
  document_public_id: z.string(),
  document_name: z.string(),
  version: z.number(),
  total_chunks: z.number(),
  chunks: z.array(ChunkSchema),
});

export type DocumentChunksResponse = z.infer<typeof DocumentChunksResponseSchema>;

// Tab enum for Knowledge Catalog navigation
export enum KnowledgeCatalogTab {
  DOCUMENT = "document",
  HISTORY = "history",
}
